import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import { motion, AnimatePresence } from 'framer-motion';
// Material-UI imports
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Link,
  Button,
  Stack,
} from '@material-ui/core';
// Utils
import { fData } from '../../utils/formatNumber';
import { getUploadValidationError } from '../../utils/fileUploadValidation';
// Components
import { MIconButton } from '../@material-extend';
import { varFadeInRight } from '../animate';
import { UploadIllustration } from '../../assets';
import { PictureAsPdf } from '@material-ui/icons';

// Styled component for the drop zone
const DropZoneStyle = styled('div')(({ theme }) => ({
  outline: 'none',
  display: 'flex',
  textAlign: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  '&:hover': { opacity: 0.72, cursor: 'pointer' },
  [theme.breakpoints.up('md')]: { textAlign: 'left', flexDirection: 'row' },
}));

// PropTypes for validation
UploadSingleFilesss.propTypes = {
  error: PropTypes.bool,
  showPreview: PropTypes.bool,
  file: PropTypes.object, // Single file object
  onRemove: PropTypes.func,
  onRemoveAll: PropTypes.func, // Not typically needed for single file
  sx: PropTypes.object,
};

// Main component
export default function UploadSingleFilesss({
  error,
  showPreview = false,
  file,
  onRemove,
  sx,
  ...other
}) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } =
    useDropzone({
      multiple: false, // Ensure single file upload
      ...other,
      validator: (selectedFile) =>
        getUploadValidationError(selectedFile) ||
        (typeof other.validator === 'function' ? other.validator(selectedFile) : null),
    });

  // Component to show rejected files
  const ShowRejectionItems = () => (
    <Paper
      variant="outlined"
      sx={{
        py: 1,
        px: 2,
        mt: 3,
        borderColor: 'error.light',
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
      }}
    >
      {fileRejections?.map(({ file, errors }) => {
        const { path, size } = file;
        return (
          <Box key={path} sx={{ my: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {path} - {fData(size)}
            </Typography>
            {errors.map((e) => (
              <Typography key={e.code} variant="caption" component="p">
                - {e.message}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Paper>
  );

  // Determine if there's a file to display
  const hasFile = file !== null;

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <DropZoneStyle
        {...getRootProps()}
        sx={{
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            color: 'error.main',
            borderColor: 'error.light',
            bgcolor: 'error.lighter',
          }),
          ...(hasFile && { padding: '12% 0' }),
        }}
      >
        <input {...getInputProps()} />

        {!hasFile && (
          <>
            <UploadIllustration sx={{ width: 220 }} />

            <Box sx={{ p: 3, ml: { md: 2 } }}>
              <Typography gutterBottom variant="h5">
                Drop or Select file
              </Typography>

              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Drop file here or click&nbsp;
                <Link underline="always">browse</Link>&nbsp;your machine
              </Typography>
            </Box>
          </>
        )}

        {/* Display preview if available */}
        {hasFile && showPreview && (
          <Stack direction="column" alignItems="center" spacing={2}>
            {file?.type?.includes('image') ? (
              <Paper
                variant="outlined"
                component="img"
                src={file?.preview}
                alt={file?.name}
                sx={{ width: 200, height: 'auto', objectFit: 'cover' }}
              />
            ) : file?.type === 'application/pdf' ? (
              <Box sx={{ position: 'relative' }}>
                <PictureAsPdf style={{ fontSize: 60, color: '#e74c3c' }} />
                <Typography variant="caption">{file?.name}</Typography>
              </Box>
            ) : (
              <Box sx={{ position: 'relative' }}>
                <Typography variant="h6">{file?.name}</Typography>
              </Box>
            )}
            {/* Remove button */}
            <IconButton
              onClick={() => onRemove()}
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                backgroundColor: alpha('#000', 0.6),
                color: '#fff',
                '&:hover': {
                  backgroundColor: alpha('#000', 0.8),
                },
              }}
            >
              <Icon icon={closeFill} />
            </IconButton>
          </Stack>
        )}
      </DropZoneStyle>

      {/* Show rejected files */}
      {fileRejections?.length > 0 && <ShowRejectionItems />}
    </Box>
  );
}
